import { useState, useMemo } from 'react';
import { useClientsWithPlazasQuery, useOpcionesTarifaQuery, useResendClientInviteMutation } from '../../services/hooks/useClientes';
import { Client } from '../../types/index';
import { Search, UserCircle, Loader2, ChevronUp, ChevronDown, Filter, Users, UserCheck, UserX, ExternalLink, Plus, Pause, Download, FileText, FileCheck, FileX, Send } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate, useParams } from 'react-router-dom';
import FiltroPlazas from '../plazas/FiltroPlazas';
import InfoTooltip from '../common/InfoTooltip';
import { formatDate, getEventBackgroundColor } from '../../utils/utils';
import { useNivelesQuery } from '../../services/hooks/useNiveles';
import ClientListAdvancedFilters from './ClientListAdvancedFilters';
import ExportClientModal from './ExportClientModal';
import { useRole } from '../../hooks/useRole';
import { CONTRATOS_ACTIVOS } from '../contratos';


// D3 — niveles de un cliente: los de sus plazas ACTIVAS, sin repetir. Quien
// tiene lunes y jueves del mismo nivel sale una vez. El endpoint ya devuelve
// `nivel_id` poblado con el nivel entero (nombre y color), así que no hace falta
// pedir nada más. No se usan los niveles de una recuperación pendiente: eso vale
// para decidir si puede reservar, no para decir en qué nivel está apuntado.
type NivelDeCliente = { _id: string; nombre: string; color?: string };

const nivelesDeCliente = (client: Client): NivelDeCliente[] => {
    const porId = new Map<string, NivelDeCliente>();
    for (const plaza of client.plazas ?? []) {
        const nivel = plaza.nivel_id;
        if (typeof nivel !== 'object' || !nivel?.nombre) continue;
        porId.set(String(nivel._id ?? nivel.nombre), {
            _id: String(nivel._id ?? nivel.nombre),
            nombre: nivel.nombre,
            color: nivel.color,
        });
    }
    return [...porId.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
};

// Filtro por tarifa: mira lo que se cobra de verdad (`desglose`), así que un
// híbrido sale en su combinación y no en "Suelo". Un precio fijo solo sale en
// "Precio fijo": su desglose, si lo tiene, ya no dice lo que paga. Desglose
// vacío es tarifa general, salvo que tenga plazas de un nivel con tarifa propia:
// entonces esa tarifa aún no se le ha aplicado.
const clienteEnTarifa = (client: Client, filtro: string, nivelesConTarifa: Set<string>): boolean => {
    const suscripciones = client.suscripciones ?? [];
    if (filtro === 'precio-fijo') return suscripciones.some((s) => s.precioFijo);

    const conTarifa = suscripciones.filter((s) => !s.precioFijo);
    const lineas = conTarifa.flatMap((s) => s.desglose ?? []);
    if (filtro === 'combinaciones') return lineas.some((l) => l.combinacionId);
    if (filtro.startsWith('combinacion:')) return lineas.some((l) => l.combinacionId === filtro.slice(12));
    if (filtro.startsWith('tarifa:')) return lineas.some((l) => l.tarifaId === filtro.slice(7));

    const niveles = nivelesDeCliente(client);
    const sinDesglose = niveles.length > 0 && conTarifa.some((s) => !s.desglose?.length);
    const nivelConTarifa = niveles.some((n) => nivelesConTarifa.has(n._id));
    if (filtro === 'sin-aplicar') return sinDesglose && nivelConTarifa;
    // 'general'
    return lineas.some((l) => !l.tarifaId && !l.combinacionId) || (sinDesglose && !nivelConTarifa);
};

interface ClientListProps {
    onSelectClient?: (client: Client) => void;
    selectable?: boolean;
}

// Definimos un tipo para los estados de filtro
type FilterStatus = 
    | 'all' 
    | 'active' 
    | 'inactive' 
    | 'waiting'
    | 'domiciliaciones'
    | 'otros-pagos'
    | 'sin-iban'
    | 'obs-cliente'
    | 'obs-suscripcion'
    | 'sin-contrato'
    | 'contrato-pendiente'
    | 'contrato-firmado'
    | 'contrato-sin-envio'
    | 'con-recuperaciones'
    | 'sin-correo'
    | 'no-invitados-portal'
    | 'pago-tarjeta'
    | 'pago-efectivo';

// Las invitaciones de Clerk caducan a los 30 días (lo dice el propio correo).
// Pasado eso, quien no ha entrado necesita otra.
const DIAS_CADUCIDAD_INVITACION = 30;

type EstadoPortal = 'sin-correo' | 'activo' | 'invitado' | 'caducada' | 'no-invitado';

const estadoPortal = (client: Client): EstadoPortal => {
    if (!client.email?.trim()) return 'sin-correo';
    if (client.portalActivo) return 'activo';
    if (!client.portalInvitadoEn) return 'no-invitado';
    const dias = (Date.now() - new Date(client.portalInvitadoEn).getTime()) / 86_400_000;
    return dias > DIAS_CADUCIDAD_INVITACION ? 'caducada' : 'invitado';
};

const AllClientList: React.FC<ClientListProps> = ({ onSelectClient, selectable = false }) => {
    // Hook para obtener el usuario actual y verificar si es superadmin
    const { isAdmin } = useRole();

    // Cambiamos el estado booleano por un enum de tres valores
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('active');
    
    
    // Usar el hook con el parámetro actualizado
    const { data: clients, isLoading, error } = useClientsWithPlazasQuery();
    const resendInvite = useResendClientInviteMutation();

    const navigate = useNavigate();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    const [searchTerm, setSearchTerm] = useState('');
    // D3 — filtro por nivel. Va aparte de `filterStatus` a propósito: aquel es una
    // lista de casos fijos escritos a mano y los niveles son datos de cada centro.
    const [filterNivel, setFilterNivel] = useState<string>('');
    const { data: niveles = [] } = useNivelesQuery();
    // Como el de nivel, va aparte de `filterStatus`: las tarifas son de cada
    // centro. Un centro sin tarifas por nivel no ve este filtro.
    const [filterTarifa, setFilterTarifa] = useState<string>('');
    const { data: opcionesTarifa } = useOpcionesTarifaQuery();
    const nivelesConTarifa = useMemo(() => new Set(opcionesTarifa?.nivelesConTarifa), [opcionesTarifa]);
    const [sortField, setSortField] = useState<keyof Client>('nombre');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Variable de estado para mostrar Filtro de Plazas
    const [showPlazasFilter, setShowPlazasFilter] = useState<boolean>(false);
    
    // Variable de estado para mostrar Modal de Exportación
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    
    // Estadísticas de clientes y contratos
    const clientStats = useMemo(() => {
        if (!clients) return { 
            total: 0, 
            active: 0, 
            inactive: 0, 
            withPlazas: 0, 
            totalPlazas: 0,
            enEspera: 0,
            contratoFirmado: 0
        };
        
        const active = clients.filter((c: Client) => c.activo).length;
        const withPlazas = clients.filter((c: Client) => c.plazas && c.plazas.length > 0).length;
        const totalPlazas = clients.reduce((sum: number, client: Client) => sum + (client.plazas?.length || 0), 0);
        const enEspera = clients.filter((c: Client) => c.enEspera === true).length;
        
        // Estadísticas de contratos
        const contratoFirmado = clients.filter((c: Client) => 
            c.contratoActivo?.estado === 'firmado'
        ).length;
        
        return {
            total: clients.length,
            active,
            inactive: clients.length - active,
            withPlazas,
            totalPlazas,
            enEspera,
            contratoFirmado
        };
    }, [clients]);
    
    // Filtrar y ordenar clientes (ajustado para el nuevo filtro)
    const filteredClients = useMemo(() => {
        if (!clients) return [];
        
        return clients
            .filter((client: Client) => {
                // Filtrar por término de búsqueda
                const searchMatch = 
                    client.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    client.dni.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    client.telefono.includes(searchTerm) ||
                    // D3: buscar "Principiantes" y no encontrar a nadie parece un fallo.
                    nivelesDeCliente(client).some((n) =>
                        n.nombre.toLowerCase().includes(searchTerm.toLowerCase()));
                
                // Filtrar por estado
                let statusMatch = true;

                // if (filterStatus === 'active') {
                //     statusMatch = client.activo === true;
                // } else if (filterStatus === 'inactive') {
                //     statusMatch = client.activo === false;
                // }

                switch (filterStatus) {
                    case 'active':
                        statusMatch = client.activo === true;
                        break;
                    case 'inactive':
                        statusMatch = client.activo === false;
                        break;
                    case 'waiting':
                        statusMatch = client.enEspera === true;
                        break;
                    case 'domiciliaciones':
                        statusMatch = client.suscripciones?.some(
                            sub => sub.nombreSuscripcion === 'pilates-domiciliacion'
                        ) || false;
                        break;
                    case 'otros-pagos':
                        statusMatch = client.suscripciones?.some(
                            sub => sub.nombreSuscripcion === 'pilates-otros-pagos'
                        ) || false;
                        break;
                    case 'sin-iban':
                        statusMatch = client.suscripciones?.some(
                            sub => sub.tipoPago === 'Domiciliaciones' && (!sub.iban || sub.iban.trim() === '')
                        ) || false;
                        break;
                    case 'obs-cliente':
                        statusMatch = !!client.observaciones && client.observaciones.trim() !== '' && client.activo === true;
                        break;
                    case 'obs-suscripcion':
                        statusMatch = (client.activo === true && client.suscripciones?.some(sub => !!sub.observaciones && sub.observaciones.trim() !== '')) || false;
                        break;
                    case 'sin-contrato':
                        statusMatch = !client.contratoActivo;
                        break;
                    case 'contrato-pendiente':
                        statusMatch = client.contratoActivo?.estado === 'pendiente' || 
                                     client.contratoActivo?.estado === 'parcialmente_firmado' || false;
                        break;
                    case 'contrato-firmado':
                        statusMatch = client.contratoActivo?.estado === 'firmado' || false;
                        break;
                    // Solo clientes activos, como las observaciones: a un cliente
                    // de baja no se le recupera, ni se le cobra, ni se le escribe.
                    case 'con-recuperaciones':
                        statusMatch = client.activo === true && (client.recuperacionesPendientes ?? 0) > 0;
                        break;
                    case 'sin-correo':
                        statusMatch = client.activo === true && !client.email?.trim();
                        break;
                    // Con correo y sin ninguna invitación válida: ni invitados
                    // nunca, ni con la invitación caducada sin haber entrado.
                    case 'no-invitados-portal': {
                        const estado = estadoPortal(client);
                        statusMatch = client.activo === true && (estado === 'no-invitado' || estado === 'caducada');
                        break;
                    }
                    case 'pago-tarjeta':
                    case 'pago-efectivo': {
                        const metodo = filterStatus === 'pago-tarjeta' ? 'Tarjeta' : 'Efectivo';
                        statusMatch = client.activo === true
                            && (client.suscripciones?.some((sub) => sub.tipoPago === metodo) || false);
                        break;
                    }
                    case 'contrato-sin-envio':
                        statusMatch = !!client.contratoActivo && 
                                     (client.contratoActivo.estado === 'pendiente' || client.contratoActivo.estado === 'parcialmente_firmado') &&
                                     (!client.contratoActivo.numeroEnvios || client.contratoActivo.numeroEnvios === 0);
                        break;
                }
                
                const nivelMatch = !filterNivel
                    || nivelesDeCliente(client).some((n) => n._id === filterNivel);

                const tarifaMatch = !filterTarifa || clienteEnTarifa(client, filterTarifa, nivelesConTarifa);

                return searchMatch && statusMatch && nivelMatch && tarifaMatch;
            })
            .sort((a: Client, b: Client) => {
                // Sin cambios en el ordenamiento
                if (sortField === 'nombre') {
                    return sortDirection === 'asc' 
                        ? a.nombre.localeCompare(b.nombre)
                        : b.nombre.localeCompare(a.nombre);
                }
                
                if (sortField === 'fechaAlta') {
                    const dateA = new Date(a.fechaAlta).getTime();
                    const dateB = new Date(b.fechaAlta).getTime();
                    return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                }
                
                return 0;
            });
    }, [clients, searchTerm, sortField, sortDirection, filterStatus, filterNivel, filterTarifa, nivelesConTarifa]);

    const handleSort = (field: keyof Client) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                Error al cargar los clientes
            </div>
        );
    }

    // Estado del acceso al portal bajo el correo y, para un admin (invitar
    // exige admin en el back), el botón para invitar sin abrir la ficha. De uno
    // en uno a propósito: Clerk deja 100 invitaciones por hora para todos los
    // centros juntos, y un envío masivo se lo comería entero.
    const renderPortal = (client: Client) => {
        const estado = estadoPortal(client);
        if (estado === 'sin-correo') return null;
        if (estado === 'activo') {
            return <div className="text-xs text-green-700">Portal activo</div>;
        }

        const enviando = resendInvite.isPending && resendInvite.variables === client._id;
        const texto = estado === 'invitado'
            ? `Invitado el ${formatDate(client.portalInvitadoEn!)}`
            : estado === 'caducada' ? 'Invitación caducada' : 'Sin invitar al portal';

        return (
            <div className={`flex items-center gap-1.5 text-xs ${estado === 'invitado' ? 'text-gray-500' : 'text-amber-700'}`}>
                <span>{texto}</span>
                {isAdmin && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            resendInvite.mutate(client._id);
                        }}
                        disabled={resendInvite.isPending}
                        className="inline-flex items-center gap-1 text-brand-primary hover:underline disabled:opacity-50"
                        title={estado === 'no-invitado' ? 'Enviar invitación al portal' : 'Reenviar invitación al portal'}
                    >
                        {enviando ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        {estado === 'no-invitado' ? 'Invitar' : 'Reenviar'}
                    </button>
                )}
            </div>
        );
    };

    // Añadir función para navegar al detalle
    const handleViewClientDetails = (client: Client, event: React.MouseEvent) => {
        // Evitar que se active onSelectClient si estamos usando el botón de detalle
        event.stopPropagation();
        navigate(`/${tenantCode}/clientes/${client._id}`);
    };

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Panel de estadísticas */}
            <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 ${CONTRATOS_ACTIVOS ? 'xl:grid-cols-7' : ''} gap-4 p-4 border-b bg-blue-50`}>
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Total Clientes</div>
                        <div className="text-xl font-bold">{clientStats.total}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <UserCheck className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Activos</div>
                        <div className="text-xl font-bold">{clientStats.active}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <UserX className="h-8 w-8 text-red-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Inactivos</div>
                        <div className="text-xl font-bold">{clientStats.inactive}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <Pause className="h-8 w-8 text-yellow-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Plazas En Espera</div>
                        <div className="text-xl font-bold">{clientStats.enEspera}</div>
                    </div>
                </div>

                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <div className="text-2xl font-bold text-purple-500 mr-3">#</div>
                    <div>
                        <div className="text-xs text-gray-500">Clientes con Plazas</div>
                        <div className="text-xl font-bold">{clientStats.withPlazas}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <div className="text-2xl font-bold text-indigo-500 mr-3">∑</div>
                    <div>
                        <div className="text-xs text-gray-500">Total Plazas</div>
                        <div className="text-xl font-bold">{clientStats.totalPlazas}</div>
                    </div>
                </div>

                {CONTRATOS_ACTIVOS && (
                    <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                        <FileCheck className="h-8 w-8 text-teal-500 mr-3" />
                        <div>
                            <div className="text-xs text-gray-500">Contratos Firmados</div>
                            <div className="text-xl font-bold">{clientStats.contratoFirmado}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Botones de acción */}
            <div className='flex justify-end gap-3 p-4'>
                <button 
                    onClick={() => setShowPlazasFilter(true)} 
                    className="bg-brand-primary hover:brightness-90 text-white py-2 px-4 rounded flex items-center gap-2"
                >
                    <Plus className="h-5 w-5" />
                    Nuevo Cliente
                </button>
                {isAdmin && (
                    <button 
                        onClick={() => setShowExportModal(true)} 
                        className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 flex items-center gap-2"
                        disabled={!clients || clients.length === 0}
                    >
                        <Download className="h-5 w-5" />
                        Exportar CSV
                    </button>
                )}
            </div>

            {/* Panel de búsqueda y filtros */}
            <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Clientes</h2>
                
                <div className="mt-2 flex flex-col md:flex-row md:flex-wrap gap-2">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, DNI, email, teléfono o nivel"
                            className="pl-10 pr-4 py-2 border rounded w-full"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    {/* D3 — filtro por nivel. Desplegable y no botón: los niveles los
                        configura cada centro, así que no hay una lista fija. */}
                    <select
                        value={filterNivel}
                        onChange={(e) => setFilterNivel(e.target.value)}
                        className="px-3 py-2 border rounded bg-white text-gray-700"
                        title="Filtrar por nivel"
                    >
                        <option value="">Todos los niveles</option>
                        {niveles.map((nivel: { _id?: string; nombre: string }) => (
                            <option key={nivel._id} value={nivel._id}>{nivel.nombre}</option>
                        ))}
                    </select>

                    {!!opcionesTarifa?.tarifas.length && (
                        <select
                            value={filterTarifa}
                            onChange={(e) => setFilterTarifa(e.target.value)}
                            className="px-3 py-2 border rounded bg-white text-gray-700"
                            title="Filtrar por tarifa"
                        >
                            <option value="">Todas las tarifas</option>
                            {opcionesTarifa.combinaciones.length > 0 && (
                                <optgroup label="Combinaciones">
                                    <option value="combinaciones">Todas las combinaciones</option>
                                    {opcionesTarifa.combinaciones.map((c) => (
                                        <option key={c._id} value={`combinacion:${c._id}`}>{c.nombre}</option>
                                    ))}
                                </optgroup>
                            )}
                            <optgroup label="Por nivel">
                                {opcionesTarifa.tarifas.map((t) => (
                                    <option key={t._id} value={`tarifa:${t._id}`}>{t.nombre}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Otras">
                                <option value="general">Tarifa general</option>
                                <option value="precio-fijo">Precio fijo</option>
                                <option value="sin-aplicar">Tarifa sin aplicar</option>
                            </optgroup>
                        </select>
                    )}

                    {/* Nuevo grupo de botones de filtro */}
                    <div className="flex flex-wrap items-center gap-1">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${
                                filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'
                            }`}
                            title="Mostrar todos los clientes"
                        >
                            <Users size={16} />
                            <span className="hidden sm:inline">Todos</span>
                        </button>
                        
                        <button
                            onClick={() => setFilterStatus('active')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${
                                filterStatus === 'active' ? 'bg-green-500 text-white' : 'bg-white text-gray-700'
                            }`}
                            title="Solo clientes activos"
                        >
                            <UserCheck size={16} />
                            <span className="hidden sm:inline">Activos</span>
                        </button>
                        
                        <button
                            onClick={() => setFilterStatus('inactive')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${
                                filterStatus === 'inactive' ? 'bg-red-500 text-white' : 'bg-white text-gray-700'
                            }`}
                            title="Solo clientes inactivos"
                        >
                            <UserX size={16} />
                            <span className="hidden sm:inline">Inactivos</span>
                        </button>

                        {/* Componente de filtros avanzados */}
                        <ClientListAdvancedFilters 
                            onFilterSelect={setFilterStatus}
                            currentFilter={filterStatus}
                        />

                    </div>
                </div>
            </div>
            <div className="px-2 py-4 bg-white border-t border-b">
                <div className="text-sm text-gray-500">
                    <strong>Mostrando {filteredClients.length}</strong> de {
                        filterStatus === 'active' ? clientStats.active :
                        filterStatus === 'inactive' ? clientStats.inactive :
                        clientStats.total
                    } clientes
                    {filterStatus !== 'all' && <span> ({filterStatus === 'active' ? 'activos' : 'inactivos'})</span>}
                    {searchTerm && <span> (filtrados por búsqueda)</span>}
                </div>
            </div>
            {filteredClients.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                    No se encontraron clientes con los filtros aplicados
                </div>
            ) : (
                <div className="w-full overflow-x-auto pb-16">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th 
                                    scope="col" 
                                    className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSort('nombre')}
                                >
                                    <div className="flex items-center">
                                        Nombre
                                        {sortField === 'nombre' && (
                                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                                        )}
                                    </div>
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    DNI
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contacto
                                </th>
                                <th 
                                    scope="col" 
                                    className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSort('fechaAlta')}
                                >
                                    <div className="flex items-center">
                                        Fecha Alta
                                        {sortField === 'fechaAlta' && (
                                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                                        )}
                                    </div>
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Plazas
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Niveles
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Detalle
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredClients.map((client: Client) => (
                                <tr 
                                    key={client._id} 
                                    className={`hover:bg-gray-50 ${selectable ? 'cursor-pointer' : ''}`}
                                    onClick={() => selectable && onSelectClient?.(client)}
                                >
                                    <td className="px-2 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <UserCircle className="h-8 w-8 text-gray-400" />
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {client.nombre}
                                                </div>
                                                {!client.activo && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                        Inactivo
                                                    </span>
                                                )}
                                                {client.enEspera && client.fechaInicioEspera && client.fechaFinEspera && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 gap-2">
                                                        En Espera 
                                                        <InfoTooltip 
                                                            text={`Desde: ${formatDate(client.fechaInicioEspera)}
                                                                Hasta: ${formatDate(client.fechaFinEspera)}
                                                                Motivo: ${client.motivoEspera || 'No especificado'}`}
                                                            iconSize={14}
                                                            position='right'
                                                        />
                                                    </span>
                                                )}
                                                {client.suscripciones?.some(sub => sub.tipoPago === 'Domiciliaciones' && (!sub.iban || sub.iban.trim() === '')) && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                        Sin IBAN
                                                    </span>
                                                )}                                                
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {client.dni}
                                    </td>
                                    <td className="px-2 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{client.telefono}</div>
                                        {client.email?.trim()
                                            ? <div className="text-sm text-gray-500">{client.email}</div>
                                            : <div className="text-xs text-gray-400 italic">Sin correo</div>}
                                        {client.activo && renderPortal(client)}
                                        {(client.recuperacionesPendientes ?? 0) > 0 && (
                                            <div className="text-xs text-emerald-700">
                                                {client.recuperacionesPendientes} recuperación{client.recuperacionesPendientes === 1 ? '' : 'es'}
                                                {client.proximaCaducidadRecuperacion && ` · caduca ${formatDate(client.proximaCaducidadRecuperacion)}`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {format(new Date(client.fechaAlta), 'dd MMM yyyy', { locale: es })}
                                    </td>
                                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {client.plazas?.length || 0} plaza(s)
                                        {client.plazas && client.plazas.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {/* D3 — el nivel sale del chip y se queda en el tooltip: lo
                                                    repetia en cada plaza cuando la columna Niveles ya lo dice
                                                    una vez, y esa anchura de mas dejaba un hueco grande antes
                                                    de la siguiente columna. */}
                                                {client.plazas.map((plaza, index) => {
                                                    const nombreNivel = typeof plaza.nivel_id === 'object'
                                                        ? plaza.nivel_id?.nombre
                                                        : undefined;
                                                    return (
                                                        <span
                                                            key={plaza._id || index}
                                                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                                                            title={`${plaza.dia} ${plaza.horaInicio} · ${nombreNivel || 'Nivel desconocido'}`}
                                                        >
                                                            {plaza.dia.substr(0, 2)} {plaza.horaInicio}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-4 text-sm text-gray-500">
                                        {/* D3 — un chip por nivel distinto. El color es apoyo, no el dato:
                                            un centro que no haya configurado colores los recibe todos
                                            iguales (el fallback de utils.ts va por nombre de nivel). */}
                                        {(() => {
                                            const susNiveles = nivelesDeCliente(client);
                                            if (susNiveles.length === 0) return <span className="text-gray-400">—</span>;
                                            return (
                                                <div className="flex flex-wrap gap-1">
                                                    {susNiveles.map((nivel) => (
                                                        <span
                                                            key={nivel._id}
                                                            className="px-2 py-1 text-xs font-medium text-white rounded-full whitespace-nowrap"
                                                            style={{ backgroundColor: getEventBackgroundColor(nivel) }}
                                                            title={nivel.nombre}
                                                        >
                                                            {nivel.nombre}
                                                        </span>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {/* Botón para ver detalles */}
                                        <button
                                            onClick={(e) => handleViewClientDetails(client, e)}
                                            className="text-blue-600 hover:text-blue-800 flex items-center"
                                            title="Ver detalle del cliente"
                                        >
                                            <ExternalLink size={16} className="mr-1" />
                                            Ver detalle
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    
                </div>
            )}
            {/* Filtro de Plazas */}
            {showPlazasFilter && (
                <FiltroPlazas
                    onClose={() => setShowPlazasFilter(false)}
                />
            )}
            
            {/* Modal de Exportación */}
            {showExportModal && clients && (
                <ExportClientModal
                    clients={clients}
                    onClose={() => setShowExportModal(false)}
                />
            )}
        </div>
    );
};

export default AllClientList;